import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ApiAdminProductStore } from '../../enums/api-admin-product-store';
import { withSiteContext } from '../../interceptors/site-context.interceptor';
import { GenericConstants } from '../../enums/generic-constants.enum';
import { CreateProductRQ } from '../../models/products/create-product-rq';
import { CartImpactCheckRQ } from '../../models/products/cart-impact-check-rq';
import { CartImpactCheckRS } from '../../models/products/cart-impact-check-rs';
import { GenerateBarcodeRQ } from '../../models/products/generate-barcode-rq';
import { GenerateBarcodeRS } from '../../models/products/generate-barcode-rs';
import { ProductRS } from '../../models/products/product-rs';
import { BulkProductStatusResult, ProductStatus } from '../../models/products/product-status';
import { BulkProductEditRequest, BulkProductEditResult, BulkProductEditPlanRequest,
  BulkProductEditPlan, BulkProductEditPlanItems } from '../../models/products/bulk-product-edit';

@Injectable({
  providedIn: 'root'
})
export class ProductService {

  constructor(private http: HttpClient) { }

  //Create New Product
  //F2/F3-1 (doc/multilanguages_products_cateogires/plan-ejecucion-tareas.md): el form de
  //detalle de producto habla el contrato Admin v3 (I18nLabel + ids opacos de opciones/valores).
  //Solo product-details.component.ts usa create/update/getByIdV3; el resto de consumidores de
  //este servicio (listado, filtros que solo muestran un título) siguen en v2 sin cambios.
  createNewProduct(siteId: string, product: CreateProductRQ) {
    let url = environment.baseUrl + ApiAdminProductStore.CREATE_PRODUCT_V3;
    let headers = new HttpHeaders({
      "Content-Type": "application/json",
      Authorization: localStorage.getItem(GenericConstants.STORAGE_ACCESS_TOKEN),
    });
    return this.http.post(url, JSON.stringify(product), { headers: headers, context: withSiteContext(siteId) });
  }

  //Import Product from URL
  importProduct(siteId: string, importRequest: any) {
    let url = environment.baseUrl + ApiAdminProductStore.IMPORT_PRODUCT;
    let headers = new HttpHeaders({
      "Content-Type": "application/json",
      Authorization: localStorage.getItem(GenericConstants.STORAGE_ACCESS_TOKEN),
    });
    return this.http.post(url, JSON.stringify(importRequest), { headers: headers, context: withSiteContext(siteId) });
  }
  
  //get product by id (v2 - String plano). Usado por el listado y por los widgets de filtro
  //(dynamic-data-filter, dynamic-property-filter-by) que solo necesitan mostrar el título como
  //string; NO tocar su contrato aquí. El form de edición usa getProductByIdV3 en su lugar.
  getProductById(siteId: string, id: string) {
    let url = environment.baseUrl + ApiAdminProductStore.GET_PRODUCT_BY_ID_V2  + id;
    let headers = new HttpHeaders({
      "Content-Type": "application/json",
      Authorization: localStorage.getItem(GenericConstants.STORAGE_ACCESS_TOKEN),
    });
    return this.http.get(url, { headers: headers, context: withSiteContext(siteId) });
  }

  //get product by id (v3 - I18nLabel + identidad estable de opciones/valores/variantes).
  //Usado exclusivamente por el form de detalle de producto al editar.
  getProductByIdV3(siteId: string, id: string) {
    let url = environment.baseUrl + ApiAdminProductStore.GET_PRODUCT_BY_ID_V3 + id;
    let headers = new HttpHeaders({
      "Content-Type": "application/json",
      Authorization: localStorage.getItem(GenericConstants.STORAGE_ACCESS_TOKEN),
    });
    return this.http.get(url, { headers: headers, context: withSiteContext(siteId) });
  }

  //update product
  updateProduct(siteId: string, product: CreateProductRQ) {
    let url = environment.baseUrl + ApiAdminProductStore.UPDATE_PRODUCT_V3;
    let headers = new HttpHeaders({
      "Content-Type": "application/json",
      Authorization: localStorage.getItem(GenericConstants.STORAGE_ACCESS_TOKEN),
    });
    return this.http.put(url, JSON.stringify(product), { headers: headers, context: withSiteContext(siteId) });
  }

  /**
   * Pide `count` códigos de barras internos libres en el site. El backend garantiza que no
   * chocan ni con lo ya guardado ni entre sí dentro del mismo lote, por eso conviene pedir los
   * de todas las variantes de golpe en vez de uno por fila.
   */
  generateBarcodes(siteId: string, count: number = 1): Observable<GenerateBarcodeRS> {
    let url = environment.baseUrl + ApiAdminProductStore.GENERATE_BARCODE_V3;
    let headers = new HttpHeaders({
      "Content-Type": "application/json",
      Authorization: localStorage.getItem(GenericConstants.STORAGE_ACCESS_TOKEN),
    });
    const body: GenerateBarcodeRQ = { site_id: siteId, count: count };
    return this.http.post<GenerateBarcodeRS>(url, JSON.stringify(body), { headers: headers, context: withSiteContext(siteId) });
  }

   //Search product
   searchProduct(
    siteId: string,
    searchParam: string = "",
    categories: string[] = [],
    sortType:string,
    productStatus:string,
    size: number = 10,
    page: number = 0,
    channels: string[] = [],
  ) {
    let url = environment.baseUrl + ApiAdminProductStore.PRODUCTS_SEARCH_V2;
    let urlenconded = new HttpParams();
    urlenconded = urlenconded.set("siteId", siteId);
    if (searchParam != "") {
      urlenconded = urlenconded.set("text", searchParam);
    }
    urlenconded = urlenconded.set("categories", categories.join(", "));
    urlenconded = urlenconded.set("size", size.toString());
    urlenconded = urlenconded.set("page", page.toString());
    if(sortType){urlenconded = urlenconded.set("sortType", sortType);}
    if(productStatus){
      urlenconded = urlenconded.set("status", productStatus);
    }
    (channels || []).forEach(c => { if (c) { urlenconded = urlenconded.append("channels", c); } });

    let headers = new HttpHeaders({
      "Content-Type": "application/json",
      Authorization: localStorage.getItem(GenericConstants.STORAGE_ACCESS_TOKEN),
    });
    return this.http.get(url, { params: urlenconded, headers: headers, context: withSiteContext(siteId) });
  }

  // F0-5 — aviso de carritos vivos (D6, doc/multilanguages_products_cateogires/
  // multi-language-architecture.md §6.5). Antes de aplicar un cambio de DIMENSION de variantes
  // (que orfana sku_id existentes), el backoffice consulta cuántos carritos activos referencian
  // esos SKUs para avisar al seller. Devuelve 0 si no hay skus/carritos.
  checkCartImpact(siteId: string, skuIds: string[]): Observable<CartImpactCheckRS> {
    let url = environment.baseUrl + ApiAdminProductStore.IMPACT_CHECK_V3;
    let headers = new HttpHeaders({
      "Content-Type": "application/json",
      Authorization: localStorage.getItem(GenericConstants.STORAGE_ACCESS_TOKEN),
    });
    const body: CartImpactCheckRQ = { site_id: siteId, sku_ids: skuIds };
    return this.http.post<CartImpactCheckRS>(url, body, { headers: headers, context: withSiteContext(siteId) });
  }

  updateProductStatus(siteId: string, id: string, status: ProductStatus): Observable<ProductRS> {
    const url = environment.baseUrl + ApiAdminProductStore.UPDATE_PRODUCT_STATUS_V2 + encodeURIComponent(id) + '/status';
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: localStorage.getItem(GenericConstants.STORAGE_ACCESS_TOKEN),
    });
    return this.http.patch<ProductRS>(url, { status }, { headers, context: withSiteContext(siteId) });
  }

  bulkUpdateProductStatus(siteId: string, ids: string[], status: ProductStatus): Observable<BulkProductStatusResult> {
    const url = environment.baseUrl + ApiAdminProductStore.BULK_UPDATE_PRODUCT_STATUS_V2;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: localStorage.getItem(GenericConstants.STORAGE_ACCESS_TOKEN),
    });
    return this.http.patch<BulkProductStatusResult>(url, { ids, status }, { headers, context: withSiteContext(siteId) });
  }

  previewBulkEdit(siteId: string, request: BulkProductEditRequest): Observable<BulkProductEditResult> {
    const url = environment.baseUrl + ApiAdminProductStore.BULK_EDIT_PREVIEW_V3;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: localStorage.getItem(GenericConstants.STORAGE_ACCESS_TOKEN),
    });
    return this.http.post<BulkProductEditResult>(url, request, { headers, context: withSiteContext(siteId) });
  }

  applyBulkEdit(siteId: string, request: BulkProductEditRequest): Observable<BulkProductEditResult> {
    const url = environment.baseUrl + ApiAdminProductStore.BULK_EDIT_APPLY_V3;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: localStorage.getItem(GenericConstants.STORAGE_ACCESS_TOKEN),
    });
    return this.http.patch<BulkProductEditResult>(url, request, { headers, context: withSiteContext(siteId) });
  }

  private bulkPlanHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: localStorage.getItem(GenericConstants.STORAGE_ACCESS_TOKEN),
    });
  }

  createBulkEditPlan(siteId: string, request: BulkProductEditPlanRequest): Observable<BulkProductEditPlan> {
    return this.http.post<BulkProductEditPlan>(environment.baseUrl + ApiAdminProductStore.BULK_EDIT_PLANS_V3,
      request, { headers: this.bulkPlanHeaders(), context: withSiteContext(siteId) });
  }

  getBulkEditPlan(siteId: string, id: string): Observable<BulkProductEditPlan> {
    return this.http.get<BulkProductEditPlan>(
      environment.baseUrl + ApiAdminProductStore.BULK_EDIT_PLANS_V3 + '/' + encodeURIComponent(id),
      { headers: this.bulkPlanHeaders(), context: withSiteContext(siteId) });
  }

  getBulkEditPlanItems(siteId: string, id: string, page = 0): Observable<BulkProductEditPlanItems> {
    return this.http.get<BulkProductEditPlanItems>(
      environment.baseUrl + ApiAdminProductStore.BULK_EDIT_PLANS_V3 + '/' + encodeURIComponent(id) + '/items',
      { headers: this.bulkPlanHeaders(), context: withSiteContext(siteId), params: new HttpParams().set('page', String(page)).set('size', '20') });
  }

  excludeBulkEditPlanItem(siteId: string, id: string, productId: string): Observable<BulkProductEditPlan> {
    return this.http.patch<BulkProductEditPlan>(
      environment.baseUrl + ApiAdminProductStore.BULK_EDIT_PLANS_V3 + '/' + encodeURIComponent(id)
      + '/items/' + encodeURIComponent(productId) + '/exclude', {},
      { headers: this.bulkPlanHeaders(), context: withSiteContext(siteId) });
  }

  includeBulkEditPlanItem(siteId: string, id: string, productId: string): Observable<BulkProductEditPlan> {
    return this.http.patch<BulkProductEditPlan>(
      environment.baseUrl + ApiAdminProductStore.BULK_EDIT_PLANS_V3 + '/' + encodeURIComponent(id)
      + '/items/' + encodeURIComponent(productId) + '/include', {},
      { headers: this.bulkPlanHeaders(), context: withSiteContext(siteId) });
  }

  executeBulkEditPlan(siteId: string, id: string): Observable<BulkProductEditPlan> {
    return this.http.post<BulkProductEditPlan>(
      environment.baseUrl + ApiAdminProductStore.BULK_EDIT_PLANS_V3 + '/' + encodeURIComponent(id) + '/execute',
      {}, { headers: this.bulkPlanHeaders(), context: withSiteContext(siteId) });
  }

}
